import moment from "moment";
import {RELATION_FIELD_TYPE} from "../model/interface/dataStorage/IField";
import {LOCALSTORAGE_MODE_KEY} from "../redux/constants/Theme";

class Utils {
    static escapeFormData(contentType, data) {
        let output = {}
        contentType?.fields.forEach(field => {
            let value = data?.hasOwnProperty(field.name) ? data[field.name] : undefined
            if (field.mode === 'relation') {
                if ([RELATION_FIELD_TYPE.MANY_TO_MANY, RELATION_FIELD_TYPE.ONE_TO_MANY].indexOf(field.type) >= 0) {
                    if (value && typeof value === 'object') {
                        value = value.map(item => {
                            return (item && item === 'object') ? item.uuid : item
                        })
                    }
                } else {
                    if (value && typeof value === 'object') {
                        value = value.uuid
                    }
                }
            }
            if (field.mode === 'scalar') {
                if (moment.isMoment(value)) {
                    value = moment(value).format('YYYY-MM-DD HH:mm:ss')
                }
                if (typeof value === 'object' && value && value.hasOwnProperty('_class') && value._class === 'DateTime') {
                    value = value.raw
                }
                if (value === undefined) {
                    value = null
                }
            }
            output[field.name] = value
        })
        return output
    }

    static cleanStorage(extra) {
        if (typeof extra === 'undefined') {
            extra = [];
        }
        Object.keys(localStorage).forEach(key => {
            if (['setup', 'user-setup', 'masquerade', 'auth_token', 'settings', 'menu', LOCALSTORAGE_MODE_KEY].indexOf(key) < 0
                || extra.indexOf(key) >= 0) {
                localStorage.removeItem(key);
            }
        })
    }

    static arrayAddOrUpdateWhere(array, conditions, item) {
        const index = Utils.findIndex(array, conditions)
        if (index > -1) {
            array[index] = item
        } else {
            array.push(item)
        }
        return array
    }

    static buildBackgroundStyles(configuration) {
        const {layers, angle, stripesAngle, stripesColor} = {
            layers: [],
            angle: 0,
            stripesAngle: 45,
            ...configuration,
            stripesColor: configuration.stripesColor || 'rgba(255,255,255,.3)',
        }
        const stripesBackground = 'linear-gradient(' + stripesAngle + 'deg, ' + stripesColor + ' 25%, transparent 25%, transparent 50%, ' + stripesColor + ' 50%, ' + stripesColor + ' 75%, transparent 75%, transparent 100%)'
        const backgroundImages = []
        const backgroundColors = []
        let backgroundColor
        const count = layers.length
        let stripes = false
        layers.forEach(layer => {
            if (layer.stripped) {
                stripes = true
            }
            backgroundColors.push(layer.color)
        })
        if (backgroundColors.length === 0) {
            // nothing
        } else if (backgroundColors.length === 1) {
            if (layers[0].stripped) {
                backgroundImages.push(stripesBackground)
            }
            backgroundColor = backgroundColors[0]
        } else if (backgroundColors.length > 1) {
            if (stripes) {
                let temp = []
                for (let i = 0; i < count - 1; i++) {
                    temp.push((layers[i].stripped ? 'transparent' : backgroundColors[i]) + ' ' + Math.round(100 * (i + 1) / count) + '%')
                    temp.push((layers[i + 1].stripped ? 'transparent' : backgroundColors[i + 1]) + ' ' + Math.round(100 * (i + 1) / count) + '%')
                }
                backgroundImages.push('linear-gradient(' + angle + 'deg, ' + temp.join(',') + ')')
                backgroundImages.push(stripesBackground)
            }
            let temp = []
            for (let i = 0; i < count - 1; i++) {
                // temp.push((layers[i].stripped ? backgroundColors[i] : 'transparent') + ' ' + Math.round(100 * (i + 1) / count) + '%')
                // temp.push((layers[i + 1].stripped ? backgroundColors[i + 1] : 'transparent') + ' ' + Math.round(100 * (i + 1) / count) + '%')
                temp.push(backgroundColors[i] + ' ' + Math.round(100 * (i + 1) / count) + '%')
                temp.push(backgroundColors[i + 1] + ' ' + Math.round(100 * (i + 1) / count) + '%')
            }
            backgroundImages.push('linear-gradient(' + angle + 'deg, ' + temp.join(',') + ')')
        }

        const output = {}
        if (backgroundImages) {
            output.backgroundImage = backgroundImages.join(',')
        }
        if (backgroundColor) {
            output.backgroundColor = backgroundColor
        }
        return output
    }

    static loadCollections(toggleLoadingCallback, updateStateCallback, services, params = {}) {
        toggleLoadingCallback(true);
        const promises = []
        const results = {}
        Object.keys(services).forEach(name => {
            promises.push(services[name].collectionList(params).then(response => {
                results[name] = response.results
            }))
        })
        return Promise.all(promises).then(() => {
            updateStateCallback(results)
            toggleLoadingCallback(false)
        })
    }

    static buildRestOptions(pagination, sorter, filters, depth = undefined, cache = undefined) {
        const options = {}
        if (pagination !== null) {
            options.page = pagination.current
            options.limit = pagination.pageSize
        }
        if (sorter && sorter.field) {
            options.order = {
                0: {
                    field: sorter.field,
                    direction: sorter.order === 'ascend' ? 'ASC' : 'DESC'
                }
            }
        }
        if (filters) {
            // TODO resolve filters?
            options.filters = filters
        }
        if (depth) {
            options.depth = depth
        }
        if (typeof cache !== 'undefined') {
            options.cache = cache
        }
        return options
    }

    /**
     * Get first character from first & last sentences of a username
     * @param {String} name - Username
     * @return {String} 2 characters string
     */
    static getNameInitial(name) {
        let initials = name.match(/\b\w/g) || [];
        return ((initials.shift() || '') + (initials.pop() || '')).toUpperCase();
    }

    /**
     * Get accessible color contrast
     * @param {String} hex - Hex color code e.g '#3e82f7'
     * @return {String} 'dark' or 'light'
     */
    static getColorContrast(hex) {
        if (!hex) {
            return 'dark'
        }
        const threshold = 130;
        const hRed = hexToR(hex);
        const hGreen = hexToG(hex);
        const hBlue = hexToB(hex);

        function hexToR(h) {
            return parseInt((cutHex(h)).substring(0, 2), 16)
        }

        function hexToG(h) {
            return parseInt((cutHex(h)).substring(2, 4), 16)
        }

        function hexToB(h) {
            return parseInt((cutHex(h)).substring(4, 6), 16)
        }

        function cutHex(h) {
            return (h.charAt(0) === '#') ? h.substring(1, 7) : h
        }

        const cBrightness = ((hRed * 299) + (hGreen * 587) + (hBlue * 114)) / 1000;
        if (cBrightness > threshold) {
            return 'dark'
        } else {
            return 'light'
        }
    }

    /**
     * Darken or lighten a hex color
     * @param {String} color - Hex color code e.g '#3e82f7'
     * @param {Number} percent - Percentage -100 to 100, positive for lighten, negative for darken
     * @return {String} Darken or lighten color
     */
    static shadeColor(color, percent) {
        let R = parseInt(color.substring(1, 3), 16);
        let G = parseInt(color.substring(3, 5), 16);
        let B = parseInt(color.substring(5, 7), 16);
        R = parseInt(R * (100 + percent) / 100);
        G = parseInt(G * (100 + percent) / 100);
        B = parseInt(B * (100 + percent) / 100);
        R = (R < 255) ? R : 255;
        G = (G < 255) ? G : 255;
        B = (B < 255) ? B : 255;
        const RR = ((R.toString(16).length === 1) ? `0${R.toString(16)}` : R.toString(16));
        const GG = ((G.toString(16).length === 1) ? `0${G.toString(16)}` : G.toString(16));
        const BB = ((B.toString(16).length === 1) ? `0${B.toString(16)}` : B.toString(16));
        return `#${RR}${GG}${BB}`;
    }

    /**
     * Convert RGBA to HEX
     * @param {String} rgba - RGBA color code e.g 'rgba(197, 200, 198, .2)')'
     * @return {String} HEX color
     */
    static rgbaToHex(rgba) {
        const trim = str => (str.replace(/^\s+|\s+$/gm, ''))
        const inParts = rgba.substring(rgba.indexOf("(")).split(","),
            r = parseInt(trim(inParts[0].substring(1)), 10),
            g = parseInt(trim(inParts[1]), 10),
            b = parseInt(trim(inParts[2]), 10),
            a = parseFloat(trim(inParts[3].substring(0, inParts[3].length - 1))).toFixed(2);
        const outParts = [
            r.toString(16),
            g.toString(16),
            b.toString(16),
            Math.round(a * 255).toString(16).substring(0, 2)
        ];

        outParts.forEach(function (part, i) {
            if (part.length === 1) {
                outParts[i] = '0' + part;
            }
        })
        return (`#${outParts.join('')}`);
    }

    /**
     * Convert HEX to RGBA
     * @param {String} hex - RGBA color code e.g 'rgba(197, 200, 198, .2)')'
     * @return {String} RGBA color
     */
    static hexaToRgba(hex) {
        const c = hex.slice(1).match(/.{1,2}/g);
        const rgb = [];
        const a = c?.[3] ? parseFloat((parseInt(((parseInt(c[3], 16) / 255) * 1000) + "") / 1000) + '') : 1
        c?.slice(0, 3).forEach(v => rgb.push(parseInt(v, 16)))
        return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${a})`
    }

    /**
     * Returns either a positive or negative
     * @param {Number} number - number value
     * @param {any} positive - value that return when positive
     * @param {any} negative - value that return when negative
     * @return {any} positive or negative value based on param
     */
    static getSignNum(number, positive, negative) {
        if (number > 0) {
            return positive
        }
        if (number < 0) {
            return negative
        }
        return null
    }

    /**
     * Returns either ascending or descending value
     * @param {Object} a - antd Table sorter param a
     * @param {Object} b - antd Table sorter param b
     * @param {String} key - object key for compare
     * @return {any} a value minus b value
     */
    static antdTableSorter(a, b, key) {
        if (typeof a[key] === 'number' && typeof b[key] === 'number') {
            return a[key] - b[key]
        }

        if (typeof a[key] === 'string' && typeof b[key] === 'string') {
            a = a[key].toLowerCase();
            b = b[key].toLowerCase();
            return a > b ? -1 : b > a ? 1 : 0;
        }
        return
    }

    /**
     * Filter array of object
     * @param {Array} list - array of objects that need to filter
     * @param {String} key - object key target
     * @param {any} value  - value that excluded from filter
     * @return {Array} a value minus b value
     */
    static filterArray(list, key, value) {
        let data = list
        if (list) {
            data = list.filter(item => item[key] === value)
        }
        return data
    }

    /**
     * Remove object from array by value
     * @param {Array} list - array of objects
     * @param {String} key - object key target
     * @param {any} value  - target value
     * @return {Array} Array that removed target object
     */
    static deleteArrayRow(list, key, value) {
        let data = list
        if (list) {
            data = list.filter(item => item[key] !== value)
        }
        return data
    }

    /**
     * Wild card search on all property of the object
     * @param {Number | String} input - any value to search
     * @param {Array} list - array for search
     * @return {Array} array of object contained keyword
     */
    static wildCardSearch(list, input) {
        const searchText = (item) => {
            for (let key in item) {
                if (item[key] == null) {
                    continue;
                }
                if (item[key].toString().toUpperCase().indexOf(input.toString().toUpperCase()) !== -1) {
                    return true;
                }
            }
        };
        list = list.filter(value => searchText(value));
        return list;
    }

    /**
     * Get Breakpoint
     * @param {Object} screens - Grid.useBreakpoint() from antd
     * @return {Array} array of breakpoint size
     */
    static getBreakPoint(screens) {
        let breakpoints = []
        for (const key in screens) {
            if (screens.hasOwnProperty(key)) {
                const element = screens[key];
                if (element) {
                    breakpoints.push(key)
                }
            }
        }
        return breakpoints
    }

    static convertArrayToFormData(data) {
        if (data instanceof FormData) {
            return data;
        }
        let formData = new FormData()
        for (const name in data) {
            if (data.hasOwnProperty(name)) {
                // console.log('aaa', name, data[name], typeof data[name], data[name] instanceof File, data[name] instanceof Blob)
                if (typeof data[name] === 'undefined') {
                    // skip
                } else if (data[name] instanceof File) {
                    formData.append(name, data[name]);
                } else {
                    let namedArray = this.convertObjectToShallowArray(data[name], name);
                    for (let i in namedArray) {
                        if (namedArray.hasOwnProperty(i)) {
                            formData.append(i, namedArray[i]);
                        }
                    }
                    if (Object.keys(namedArray).length === 0) {
                        formData.append(name, '');
                    }
                }
                // formData.append(name, data[name]);
            }
        }
        return formData
    }

    static setAllUndefinedPropertiesToNull(obj) {
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                if (typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
                    obj[key] = this.setAllUndefinedPropertiesToNull(obj[key]);
                } else if (Array.isArray(obj[key])) {
                    obj[key] = obj[key].map(item => {
                        return (typeof item === 'object') ? this.setAllUndefinedPropertiesToNull(item) : item;
                    });
                } else if (obj[key] === undefined) {
                    obj[key] = null;
                }
            }
        }
        return obj;
    }

    static convertObjectToShallowArray(value, prefix) {
        let output = []
        if (typeof value === 'object' && value !== null) {
            for (let i in value) {
                if (value.hasOwnProperty(i)) {


                    let namedArray = this.convertObjectToShallowArray(value[i], this.buildFormDataDataName(prefix, i));
                    for (let j in namedArray) {
                        if (namedArray.hasOwnProperty(j) && typeof namedArray[j] !== 'undefined') {
                            output[j] = namedArray[j]
                        }
                    }

                    // for(let j in namedArray) {
                    // 	if(namedArray.hasOwnProperty(j)) {
                    // 		//formData.append(i, namedArray[i]);
                    // 		output[this.buildFormDataDataName(prefix, i)] = this.convertObjectToShallowArray(value, this.buildFormDataDataName(prefix, i));
                    // 	}
                    // }
                    //
                }
            }
            if (Object.keys(value).length === 0) {
                output[prefix] = ''
            }
        } else {
            output[prefix] = value;
        }
        return output;
    }

    static buildFormDataDataName(prefix, name) {
        return prefix + '[' + name + ']';
    }

    static findIndex(haystack, requirements) {
        const requirementsKeys = Object.keys(requirements)
        let index = -1
        if (haystack) {
            haystack.forEach((item, i) => {
                let match = true
                requirementsKeys.forEach(name => {
                    if (typeof requirements[name] === 'object'
                        && typeof item[name] === 'object') {
                        if (!this.objectMatches(item[name], requirements[name])) {
                            match = false;
                        }
                    } else if (item[name] !== requirements[name]) {
                        match = false;
                    }
                })
                if (match) {
                    index = i
                }
            })
        }
        return index
    }

    static objectMatches(object, requirements) {
        if (typeof object !== 'object' || typeof requirements !== 'object') {
            return false;
        }
        let match = true
        Object.keys(requirements).forEach(i => {
            if (object[i] !== requirements[i]) {
                match = false;
                return false;
            }
        })
        return match
    }

    static toBoolean(string) {
        return string && string !== 'undefined' ? JSON.parse(string) : false
    }

    static isNull(string) {
        return (!string || string === 'undefined') && string !== 0
    }

    static compareTemporaryApiObjects(first, second) {
        if (!first || !second) {
            return false
        }
        if (typeof first === 'number') {
            if (typeof second === 'number') {
                return first === second
            }
            return first === (second.id ? second.id : second.api_temp_id)
        }
        if (typeof second === 'number') {
            return first.id === second
        }
        return (first.id ? first.id : first.api_temp_id) === (second.id ? second.id : second.api_temp_id)
    }

    /**
     * Get uuid
     * @return {string}
     */
    static uuid = () => {
        return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
            (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
        );
    }

    static isUuid = (text) => {
        return !!text && typeof text === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(text)
    }

    static isObjectIdentifiedBy = (object, identifier) => {
        return Utils.isUuid(identifier) ? object.uuid === identifier : object.id?.toString() === identifier?.toString()
    }

    static removeFromArrayWhere = (array, conditions) => {
        const index = this.findIndex(array, conditions);
        if (index < 0) {
            throw new Error('Element with provided requirements not found')
        }
        array.splice(index, 1)
        return array
    }

    static stripHtml(html) {
        let tmp = document.createElement("DIV");
        tmp.innerHTML = html ? html : '';

        return tmp.textContent || tmp.innerText || "";
    }

    static imageFallback() {
        return "data:image/png;base64,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"
    }

    /**
     * Replace all wildcards with number _identifier_, then match as substring or use original string
     * @param definition
     * @param current
     * @param strict
     */
    static urlMatches(definition, current, strict = false) {
        const normalizedDefinition = definition.split('/').map(part => part.charAt(0) === ':' ? '_identifier_' : part).join('/')
        if (normalizedDefinition.includes()) {

        }
        if (strict) {
            return normalizedDefinition === current.replaceAll(/(\/\w{8}-\w{4}-\w{4}-\w{4}-\w{12})|(\/[0-9]+)/g, '/_identifier_')
        }
        return normalizedDefinition.indexOf(current.replaceAll(/(\/\w{8}-\w{4}-\w{4}-\w{4}-\w{12})|(\/[0-9]+)/g, '/_identifier_')) >= 0
            || definition.indexOf(current) >= 0
    }

    static isTargetEntityContentType(className) {
        return className && className.match(/^App\\DataStorage\\Virtual\\Entity\\/)
    }

    static decimalSeparator() {
        let n = 1.1;
        n = n.toLocaleString().substring(1, 2);
        return n;
    }

    static adjustColor(color, amount) {
        return '#' + color.replace(/^#/, '').replace(/../g, color => ('0' + Math.min(255, Math.max(0, parseInt(color, 16) + amount)).toString(16)).substr(-2));
    }

    static getScreenInfo() {
        // const mobileRegex1 = /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i
        // const mobileRegex2 = /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i
        // // @ts-ignore
        // const browser = navigator.userAgent||navigator.vendor||window.opera
        // // @ts-ignore
        // const isMobile = mobileRegex1.test(browser.substr(0,4)) || mobileRegex2.test(browser.substr(0,4))
        const isMobile = window.innerWidth < 800
        // console.log('innerWidth', window.innerWidth)
        return {
            // screen: {
            width: window.innerWidth,
            height: window.innerHeight,
            isMobile,
            isLandscape: window.innerWidth > window.innerHeight,
            isPortrait: window.innerWidth < window.innerHeight,
            // }
        }
    }

    static registerScreenUpdate(timeoutUpdateCallback, screenTimeoutCallback) {
        window.addEventListener('resize', () => {
            // if(this.state.screenTimeout) {
            //     clearTimeout(this.state.screenTimeout!);
            // }
            timeoutUpdateCallback(setTimeout(() => {
                screenTimeoutCallback(Utils.getScreenInfo())
            }, 300))
        })
    }

    static getSettings(settings, pathArray, defaultValue) {
        let current = settings
        for (let key in pathArray) {
            if (pathArray.hasOwnProperty(key)) {
                if (current.hasOwnProperty(pathArray[key])) {
                    current = current[pathArray[key]]
                    if (parseInt(key, 10) === pathArray.length - 1) {
                        return current
                    }
                } else {
                    return defaultValue
                }
            }
        }
        return defaultValue
    }

    static escapeDiacritic(text, toLower = true) {
        const escaped = text.normalize("NFD").replace(/\p{Diacritic}/gu, "")
        return toLower ? escaped.toLowerCase() : escaped
    }

    static stringContains(source, needle, escaped = true, toLower = true) {
        return source && needle && Utils.escapeDiacritic(source, toLower).indexOf(Utils.escapeDiacritic(needle, toLower)) >= 0
    }

    static stringStartsWith(source, needle, escaped = true, toLower = true) {
        return source && needle && Utils.escapeDiacritic(source, toLower).indexOf(Utils.escapeDiacritic(needle, toLower)) === 0
    }

    static downloadFile(blob, name) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', name)
        document.body.appendChild(link)
        link.click()
        link.parentNode?.removeChild(link)
    }

    static isValidHttpUrl(text) {
        let url;

        try {
            url = new URL(text || '');
        } catch (_) {
            return false;
        }

        return url.protocol === "http:" || url.protocol === "https:";
    }

    static camelize(str) {
        return str.replace(/(?:^\w|[A-Z]|\b\w)/g, function (word, index) {
            return index === 0 ? word.toLowerCase() : word.toUpperCase();
        }).replace(/\s+/g, '');
    }

    static parseObjectToIdentifier(value, identifier = 'id') {
        if (Array.isArray(value)) {
            const array = []
            value.forEach((rawId) => {
                array.push(this.parseObjectToIdentifier(rawId, identifier))
            })
            return array
        } else {
            return value && typeof value === 'object' && identifier in value ? value[identifier] : value
        }
    }

    static hexToRgbA(hex, opacity = 1) {
        if (hex && /^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
            let c = hex.substring(1).split('');
            if (c.length == 3) {
                c = [c[0], c[0], c[1], c[1], c[2], c[2]];
            }
            c = '0x' + c.join('');
            return 'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') + ',' + opacity + ')';
        }
        return hex
    }

    static rgbaToHexNoA(color) {
        const values = color
            .replace(/rgba?\(/, '')
            .replace(/\)/, '')
            .replace(/[\s+]/g, '')
            .split(',');
        const a = parseFloat(values[3] || 1),
            r = Math.floor(a * parseInt(values[0]) + (1 - a) * 255),
            g = Math.floor(a * parseInt(values[1]) + (1 - a) * 255),
            b = Math.floor(a * parseInt(values[2]) + (1 - a) * 255);
        return "#" +
            ("0" + r.toString(16)).slice(-2) +
            ("0" + g.toString(16)).slice(-2) +
            ("0" + b.toString(16)).slice(-2);
    }

    static hexOpacity(hex, opacity = 1) {
        return this.rgbaToHexNoA(this.hexToRgbA(hex, opacity))
    }

    static hexaOpacity(hex, opacity = 1) {
        return Utils.hexOpacity(Utils.rgbaToHexNoA(Utils.hexaToRgba(hex)), opacity)
    }

    static isObject = (item) => {
        return (item && typeof item === 'object' && !Array.isArray(item));
    }

    static deepMerge = (target, ...sources) => {
        if (Array.isArray(target) && !target.length) target = {}
        if (!sources.length) return target;
        const source = sources.shift();
        if (Array.isArray(target) && Array.isArray(source)) {
            for (const key in source) {
                if (target[key]) {
                    target[key] = this.deepMerge(target[key], source[key]);
                } else {
                    target.push(source[key])
                }
            }
        }
        if (Utils.isObject(target) && Utils.isObject(source)) {
            for (const key in source) {
                if (typeof source[key] === 'object') {
                    target[key] = this.deepMerge(target[key], source[key]);
                } else {
                    Object.assign(target, {
                        [key]: source[key]
                    });
                }
            }
        }
        return Utils.deepMerge(target, ...sources);
    }

    static previousMonth = (date = null) => {
        const now = date ? date : moment()
        if (now.month() === 0) {
            return now.month(11).year(now.year() - 1)
        }
        return now.month(now.month() - 1)
    }

    static localeDecimalSeparator() {
        let n = 1.1;
        n = n.toLocaleString().substring(1, 2);
        return n;
    }

    /**
     * Uses canvas.measureText to compute and return the width of the given text of given font in pixels.
     *
     * @param text The text to be rendered.
     * @param {String} font The css font descriptor that text is to be rendered with (e.g. "14px verdana").
     *
     * @see http://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
     */
    static getVisualTextWidth(text, font = null) {
        const canvas = this.visualTextCanvas || (this.visualTextCanvas = document.createElement("canvas"));
        const context = canvas.getContext("2d");
        context.font = font || Utils.getCanvasFont()
        const metrics = context.measureText(text);
        return metrics.width;
    }

    static getCssStyle(element, prop) {
        return window.getComputedStyle(element, null).getPropertyValue(prop);
    }

    static getCanvasFont(el = document.body) {
        const fontWeight = Utils.getCssStyle(el, 'font-weight') || 'normal';
        const fontSize = Utils.getCssStyle(el, 'font-size') || '16px';
        const fontFamily = Utils.getCssStyle(el, 'font-family') || 'Times New Roman';

        return `${fontWeight} ${fontSize} ${fontFamily}`;
    }
}

export default Utils;